import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sale, SaleAdditionalItem, SaleOffer } from '../../api/SalesAPI';
import { Item } from '../../api/CollectionsAPI';
import { ItemAutocomplete } from '../ItemAutocomplete/ItemAutocomplete';
import SaleItemLine from '../SaleItemLine/SaleItemLine';
import CONFIG from '../../config/config';
import {
  applyAdditionalItemOffer,
  hasSaleLevelOffer,
  isOfferPresent,
  withPricingRecalc,
} from '../../utils/saleLineItems';

type Props = {
  sale: Sale;
  onChange: (updates: Partial<Sale>) => void;
  onCurrencyChange: (currency: string) => void;
  isDisabled?: boolean;
};

function SaleAdditionalItemsSection({ sale, onChange, onCurrencyChange, isDisabled = false }: Props) {
  const { t } = useTranslation();
  const items = useMemo(() => sale.additionalItems ?? [], [sale.additionalItems]);
  const saleLevelActive = hasSaleLevelOffer(sale);
  const [expandedOfferIndex, setExpandedOfferIndex] = useState<number | null>(null);

  const updateItems = useCallback(
    (next: SaleAdditionalItem[]) => {
      onChange(
        withPricingRecalc(sale, {
          additionalItems: next,
        })
      );
    },
    [onChange, sale]
  );

  const handleAddItem = useCallback(
    (item: Item) => {
      const line: SaleAdditionalItem = {
        itemName: item.name,
        productId: item.id?.toString(),
        productType: 'CATALOG_ITEM',
        linePrice: item.price ?? 0,
        quantity: 1,
        sortOrder: items.length,
      };
      updateItems([...items, line]);
    },
    [items, updateItems]
  );

  const handleRemove = (index: number) => {
    if (expandedOfferIndex === index) {
      setExpandedOfferIndex(null);
    }
    updateItems(items.filter((_, i) => i !== index));
  };

  const handleLinePriceChange = (index: number, linePrice: number) => {
    updateItems(
      items.map((row, i) => (i === index ? { ...row, linePrice } : row))
    );
  };

  const handleItemOfferChange = (index: number, offer: SaleOffer | undefined) => {
    onChange(applyAdditionalItemOffer(sale, index, offer));
    if (!isOfferPresent(offer)) {
      setExpandedOfferIndex(null);
    }
  };

  const lineLevelDisabledHint = saleLevelActive
    ? t('sales.form.offer.line_level_disabled') ||
      'Line offers are disabled while a sale-level offer is active.'
    : undefined;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-900">
        {t('sales.form.product.additional_items') || 'Additional items'}
      </h4>
      {!isDisabled && (
        <ItemAutocomplete
          onChange={handleAddItem}
          placeholder="contracts.model.search.placeholder"
          defaultValue=""
        />
      )}
      {items.length > 0 && (
        <ul className="rounded-md border border-gray-200 divide-y divide-gray-200 bg-white text-sm">
          {items.map((row, index) => {
            const grossLine = (row.linePrice ?? 0) * Math.max(row.quantity ?? 1, 1);
            const isOfferExpanded = expandedOfferIndex === index;
            return (
              <SaleItemLine
                key={row.id ?? `additional-${index}`}
                itemName={row.itemName}
                productVariant={row.productVariant}
                price={row.linePrice ?? 0}
                currency={sale.currency}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={onCurrencyChange}
                onPriceChange={(price) => handleLinePriceChange(index, price)}
                offer={row.offer}
                baseAmount={grossLine}
                onOfferChange={(offer) => handleItemOfferChange(index, offer)}
                isOfferDisabled={saleLevelActive}
                disabledHint={lineLevelDisabledHint}
                isOfferExpanded={isOfferExpanded}
                onOfferExpandedChange={(expanded) =>
                  setExpandedOfferIndex(expanded ? index : null)
                }
                onRemoveItem={() => handleRemove(index)}
                removeItemAriaLabel={
                  t('sales.form.product.remove_additional_item') || 'Remove item'
                }
                isDisabled={isDisabled}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default SaleAdditionalItemsSection;
